import { FunctionComponent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { observer } from "mobx-react-lite";

import { Menu } from "primereact/menu";
import { classNames } from "primereact/utils";

import { faArrowRightFromBracket, faBug, faGear, faUser } from "@fortawesome/pro-regular-svg-icons";

import { AppRole } from "../../../../@api";

import { applicationConfig, appVersion } from "../../../../environmentConfiguration";
import { translationKeys } from "../../../models/app-translation-keys";
import { RouteConstants } from "../../../models/enums";
import { IMenuItem } from "../../../models/interfaces";
import { useStore } from "../../../stores/StoreContext";
import { ActionButton } from "../../Shared/Buttons";
import { Icon } from "../../Shared/Icon/Icon";
import { useBugReportDialog } from "./BugReportDialog/useBugReportDialog";

import "./UserProfile.scss";

const versionInfoClass = "version-info";

const RawUserProfile: FunctionComponent = () => {
  const { t } = useTranslation();
  const { userStore, sidebarStore, breakpointStore, permissionStore } = useStore();
  const navigate = useNavigate();

  const [menuVisible, setMenuVisible] = useState(false);
  const [bugReportVisible, setBugReportVisible] = useState(false);

  const userProfileOverlayPanelRef = useRef<Menu>(null);

  const [menuItemsRaw, setMenuItemsRaw] = useState<IMenuItem[]>([]);
  const [menuItems, setMenuItems] = useState<IMenuItem[]>([]);

  const isUserInfoVisible = sidebarStore.isOverlaySidebar(breakpointStore.device);

  useEffect(() => {
    const menuItemsRaw = [
      {
        id: "user-info",
        label: userStore.userInfo?.displayName,
        fontAwesomeIcon: faUser,
        visible: isUserInfoVisible,
        disabled: true,
        hideIcon: true,
      },
      permissionStore.hasAnyAdminRole()
        ? {
            id: "admin-area",
            label: translationKeys.navigation.goToAdminArea,
            url: RouteConstants.AdminArea,
            path: RouteConstants.AdminArea,
            fontAwesomeIcon: faGear,
          }
        : undefined,
      permissionStore.hasRole(AppRole.ChatbotFeature)
        ? {
            id: "userSettings-area",
            label: translationKeys.navigation.goToUserSettingsArea,
            url: RouteConstants.UserSettingsArea,
            path: RouteConstants.UserSettingsArea,
            fontAwesomeIcon: faUser,
          }
        : undefined,
      applicationConfig.featureFlags.enableReportABug
        ? {
            id: "bug-report",
            label: translationKeys.bugReport.menuItemLabel,
            fontAwesomeIcon: faBug,
            command: () => setBugReportVisible(true),
          }
        : undefined,
      {
        id: "logout",
        label: translationKeys.global.logout,
        url: RouteConstants.Logout,
        path: RouteConstants.Logout,
        fontAwesomeIcon: faArrowRightFromBracket,
      },
      {
        id: "version",
        label: appVersion,
        disabled: true,
        className: versionInfoClass,
      },
    ].filter(Boolean) as IMenuItem[];

    setMenuItemsRaw(menuItemsRaw);
  }, [sidebarStore.isOverlaySidebar(breakpointStore.device)]);

  const menuItemTemplate = (item: any) => {
    if (item.className === versionInfoClass) {
      return (
        <div className={versionInfoClass}>
          <span>{item.label}</span>
        </div>
      );
    }

    const handleClick = (event: any) => {
      if (item.disabled) {
        return;
      }
      if (item.command) {
        item.command({ originalEvent: event, item });
      } else {
        navigateTo(event, item.url ?? "");
      }
    };

    return (
      <div
        className={classNames("menu-link", {
          disabled: item.disabled,
          "has-navigation": !item.url?.length && !item.command,
        })}
        onClick={handleClick}
        onKeyDown={event => event.key === "Enter" && handleClick(event)}
        tabIndex={0}
        role="button"
      >
        {!item.hideIcon && (
          <div className="menu-link-icon">
            <Icon icon={item.fontAwesomeIcon} />
          </div>
        )}
        <span>{t(item?.label ?? "")}</span>
      </div>
    );
  };

  useEffect(() => {
    setMenuItems(
      menuItemsRaw.map(item => {
        return {
          ...item,
          template: menuItemTemplate,
        };
      }),
    );
  }, [menuItemsRaw, userStore.currentLanguage]);

  const toggleUser = (event: any) => {
    if (userProfileOverlayPanelRef.current) {
      userProfileOverlayPanelRef.current.toggle(event);
    }
  };

  const navigateTo = (_event: any, path: RouteConstants) => {
    navigate(path.startsWith("/") ? path : `/${path}`);
  };

  useBugReportDialog({ isVisible: bugReportVisible, onHide: () => setBugReportVisible(false) });

  return (
    <div className="user-profile">
      <ActionButton
        id="user-profile-action-button"
        onClick={event => toggleUser(event)}
        icon={faUser}
        label={!isUserInfoVisible ? userStore.userInfo?.displayName : undefined}
        showBackground={isUserInfoVisible}
        focused={menuVisible}
      />

      <Menu
        model={menuItems}
        popup={true}
        ref={userProfileOverlayPanelRef}
        id="popup_menu_right"
        popupAlignment="right"
        onShow={() => {
          setMenuVisible(true);
        }}
        onHide={() => {
          setMenuVisible(false);
        }}
      />
    </div>
  );
};

export const UserProfile = observer(RawUserProfile);
